import { apiFetch } from '@ami/react/core';
import {
  CreateQuoteStep1,
  QuoteProduct,
  QuoteCustomLineItem,
  QuoteAttachmentPoint,
  QuoteInstallation,
  QuoteProductOption,
  QuoteProductDimension,
  QuoteProductCustomization,
  QuoteOptInDiscount,
} from './otherQuoteEntities';
import { NotificationManager } from 'react-notifications';
import { QuoteInstallationSummaryCalculator } from '../quotes/quoteInstallationInformation';
import _ from 'lodash';
import { appConfig as config } from '@ami/react/core';
import { roundNumberToNearestCent } from '@ami/react/core';
import OverallDiscount from '../overallDiscount';
import NotificationSettings from '../notificationSettings';
import { isAmiUserAuthorizedToManageDealerUsers } from '../authorization/amiPermissions';
import { isUserDealerAdministrator } from '../authorization/permissions';

const baseUrl = 'quotes';
const subscriptionsBaseUrl = 'quote-subscriptions';
function validateFetch(target, body) {
  return apiFetch(`${baseUrl}/validations/${target}`, 'POST', body, {
    automaticSuccessNotification: false,
  });
}

export class Quote {
  constructor() {
    this.id = null;
    this.dealerUserId = null;
    this.salesPersonId = null;
    this.billingInstructions ='';
    this.shippingInstructions ='';
    this.territoryManagerId = null;
    this.name = '';
    this.notes = '';
    this.privateNotes = '';
    this.customShippingLocation = null;
    this.step1 = null;
    this.products = [];
    this.customLineItems = [];
    this.attachmentPoints = [];
    this.installation = undefined;
    this.supportsInstallation = false;
    this.canHaveFreightSurcharge = true;
    this.priceConfiguration = { dealerDiscountPercentage: 0, taxRate: 0 };
    this.freightSurcharge = undefined;
    this.freightSurchargeOverridden = false;
    this.installationInformation = null;
    this.installationSummaryEntries = [];
    this.expeditedShippingRequested = false;
    this.expeditedShippingNotes = '';
    this.overallDiscounts = [];
    this.notificationSettings = new NotificationSettings();
    this.quoteGroupId = null;
    this.quoteGroupName = '';
    this.industryId = null;
    this.industryName = '';
    this.optInDiscounts = [];
    this.useLatestDiscounts = true;
    this.maskPricingAndLeadTimes = false;
    this.hasOpenCustomInstructionTask = false;
  }

  toQueryString() {
    return Object.getOwnPropertyNames(this)
      .filter((x) => this[x])
      .map((x) => {
        if (Array.isArray(this[x])) {
          return this[x].map((y) => `${x}=${y}`).join('&');
        } else {
          return `${x}=${this[x]}`;
        }
      })
      .join('&');
  }

  clone() {
    return _.cloneDeep(this);
  }

  calculateInstallationSummaryEntries() {
    if (!this.supportsInstallation) {
      throw new Error("Quote doesn't support installation");
    }
    const quoteInstallationSummaryCalculator =
      new QuoteInstallationSummaryCalculator(
        this.installationInformation,
        this.priceConfiguration.dealerDiscountPercentage
      );
    this.installationSummaryEntries =
      quoteInstallationSummaryCalculator.calculateFor(this);
    if (this.installationSummaryEntries.length === 0) {
      this.installation.setIncluded(false);
    }
  }

  setPriceConfiguration(dealerDiscountPercentage, taxRate) {
    this.priceConfiguration = { dealerDiscountPercentage, taxRate };
  }

  setInstallationInformation(quoteInstallationInformation) {
    if (!quoteInstallationInformation) return;

    this.supportsInstallation = true;
    this.installationInformation = quoteInstallationInformation;
    this.installation = new QuoteInstallation(this.installation);
    this.calculateInstallationSummaryEntries();
  }

  setWeightCategoryId(weightCategoryId) {
    this.step1.weightCategoryId = weightCategoryId;
  }

  setMachine(machine) {
    this.step1.machineId = machine.id;
    this.step1.weightCategoryId = machine.weightCategoryId;

    // We set the model property for the Step3 header.
    this.step1.model = machine.model;
  }

  optOutDiscount(discountId) {
    this.setUseLatestDiscounts();
    this.optInDiscounts = this.optInDiscounts.filter(x => x.discountId !== discountId);
  }

  optInDiscount(discountId, documentationFileKey, documentationFileName) {
    const discount = this.optInDiscounts.find(
      (x) => x.discountId === discountId
    );

    if (discount) {
      discount.documentationFileKey = documentationFileKey;
      discount.documentationFileName = documentationFileName;
    } else {
      this.setUseLatestDiscounts();
      this.optInDiscounts.push(new QuoteOptInDiscount({
        discountId: discountId,
        documentationFileKey: documentationFileKey,
        documentationFileName: documentationFileName
      }));
    }
  }

  setUseLatestDiscounts() {
    this.useLatestDiscounts = true;
  }

  addProduct(quoteProduct) {
    quoteProduct.id = new Date().getTime();
    this.products.push(quoteProduct);
    if (
      this.attachmentPoints.some((x) => x.id === quoteProduct.attachmentPointId)
    )
      return;
    this.attachmentPoints.push(
      new QuoteAttachmentPoint(quoteProduct.attachmentPointId)
    );
  }

  removeProduct(productDtoId) {
    const productToRemove = this.products.find((x) => x.id === productDtoId);
    this.products = this.products.filter((x) => x !== productToRemove);
  }

  updateProductQuantity(quoteProductId, quantity) {
    const product = this.products.find((x) => x.id === quoteProductId);
    product.quantity = quantity;

    if (this.supportsInstallation) {
      this.calculateInstallationSummaryEntries();
    }
  }

  updateCustomLineItemQuantity(quoteCustomLineItemId, quantity) {
    const customLine = this.customLineItems.find((x) => x.id === quoteCustomLineItemId);
    customLine.quantity = quantity;
  }

  addProductCustomization(
    quoteProductId,
    customization
  ) {
    const product = this.products.find((x) => x.id === quoteProductId);
    product.customizations = [...product.customizations, customization];
  }

  removeProductCustomization(
    quoteProductId,
    customizationIdx
  ) {
    const product = this.products.find((x) => x.id === quoteProductId);
    product.customizations = [
      ...product.customizations.slice(0, customizationIdx),
      ...product.customizations.slice(customizationIdx + 1),
    ];
  }

  setProductOverrideDiscount(
    quoteProductId,
    overrideDiscount
  ) {
    const product = this.products.find((x) => x.id === quoteProductId);
    product.userDefinedDiscount = overrideDiscount;
  }

  removeProductOverrideDiscount(
    quoteProductId
  ) {
    const product = this.products.find((x) => x.id === quoteProductId);
    product.userDefinedDiscount = null;
  }

  updateProduct(quoteProduct) {
    this.products = this.products.map((x) =>
      x.id === quoteProduct.id ? quoteProduct : x
    );
    if (this.supportsInstallation) {
      this.calculateInstallationSummaryEntries();
    }
  }

  updateProductsPricing(quoteProductsPricing) {
    this.products.map((x) => {
      const quoteProductPricing = quoteProductsPricing.find(y => y.id === x.id || x.id.toString() === y.id);
      if (quoteProductPricing) {
        const pricing = quoteProductPricing.pricing;

        x.pricing = pricing;
        x.dNetPrice = pricing.dNetPrice;
        x.msrp = pricing.msrp;
        x.totalPrice = pricing.totalPrice;
        x.totalMsrp = pricing.totalMsrp;
        x.productOptions.map((y) => {
          const pricingSubComponent = pricing.subComponents.find(z => z.correlationId === y.productOptionId);
          if(pricingSubComponent) {
            y.dNetPrice = pricingSubComponent.dNetPrice;
            y.msrp = pricingSubComponent.msrp;
          }
        });
      }
    });
  }

  updateCustomLineItem(quoteCustomLineItem) {
    this.customLineItems = this.customLineItems.map((x) =>
      x.id === quoteCustomLineItem.id ? quoteCustomLineItem : x
    );
  }

  updateCustomLineItemsPricing(quoteCustomLineItemsPricing) {
    this.customLineItems.map((x) => {
      const quoteCustomLineItemPricing = quoteCustomLineItemsPricing.find(y => y.id === x.id || x.id.toString() === y.id);
      if (quoteCustomLineItemPricing) {
        const pricing = quoteCustomLineItemPricing.pricing;

        x.pricing = pricing;
        x.dNetPrice = pricing.dNetPrice;
        x.msrp = pricing.msrp;
        x.totalPrice = pricing.totalPrice;
        x.totalMsrp = pricing.totalMsrp;
      }
    });
  }

  addCustomLineItem(item) {
    this.customLineItems = [...this.customLineItems, item];
  }

  removeCustomLineItem(id) {
    this.customLineItems = this.customLineItems.filter((x) => x.id !== id);
  }

  setCustomLineItemOverrideDiscount(
    customLineItemId,
    overrideDiscount
  ) {
    const customLineItem = this.customLineItems.find((x) => x.id === customLineItemId);
    customLineItem.userDefinedDiscount = overrideDiscount;
  }

  removeCustomLineItemOverrideDiscount(
    customLineItemId
  ) {
    const customLineItem = this.customLineItems.find((x) => x.id === customLineItemId);
    customLineItem.userDefinedDiscount = null;
  }

  updateAttachmentPoint(attachmentPoint) {
    this.attachmentPoints = this.attachmentPoints.map((x) =>
      attachmentPoint.id === x.id ? attachmentPoint : x
    );
  }

  updateAttachmentPointsCouplerInformation(attachmentPointDataItems) {
    this.attachmentPoints.forEach((attachmentPoint) => {
      const dataItem = attachmentPointDataItems.find(
        (x) => x.attachmentPointId === attachmentPoint.id
      );
      if (dataItem) attachmentPoint.updateCouplerInformation(dataItem);
    });
  }

  updateAttachmentPointsThumbMount(attachmentPoint, machineOemThumbMount) {
    const attachmentPointsFiltered = this.attachmentPoints.filter(
      (x) => x.id === attachmentPoint.attachmentPointId
    );
    attachmentPointsFiltered.forEach((attachmentPoint) => {
      attachmentPoint.setThumbMount(machineOemThumbMount, null, null);
    });
  }

  updateProductInventoryItems(inventoryItems) {
    const changes = [];
    this.products.forEach((quoteProduct) => {
      if (
        quoteProduct.inventoryItemId &&
        !inventoryItems.some((x) => x.id === quoteProduct.inventoryItemId)
      ) {
        quoteProduct.inventoryItemId = null;
        changes.push({
          name: quoteProduct.groupName,
          partNumber: quoteProduct.partNumber,
        });
      }
    });
    return changes;
  }

  validateStep1() {
    return validateFetch('step-1', this.step1);
  }

  validateProduct(quoteProduct) {
    return validateFetch('product', quoteProduct);
  }

  getInstallationPayload() {
    if (!this.supportsInstallation) return null;

    const installation = this.installation;
    return {
      included: installation.included,
      location: installation.location,
      travelExpense: installation.travelExpense,
      type: installation.type,
      areInstallationOptionsCurrent: this.installationInformation.isCurrent,
    };
  }

  getProductsSubtotal() {
    return roundNumberToNearestCent(
      this.products.reduce(
        (prevTotal, product) => prevTotal + product.pricing.totalPrice,
        0
      )
    );
  }

  getProductsSubtotalMsrp() {
    return roundNumberToNearestCent(
      this.products.reduce(
        (prevTotal, product) => prevTotal + product.pricing.totalMsrp,
        0
      )
    );
  }

  getCustomLineItemsSubTotal() {
    return roundNumberToNearestCent(
      this.customLineItems.reduce(
        (prevTotal, item) => prevTotal + (item.pricing ? item.pricing.totalPrice : 0),
        0
      )
    );
  }

  getCustomLineItemsSubTotalMsrp() {
    return roundNumberToNearestCent(
      this.customLineItems.reduce(
        (prevTotal, item) => prevTotal + (item.pricing ? item.pricing.totalMsrp : 0),
        0
      )
    );
  }

  getInstallationSubTotal() {
    if (!this.supportsInstallation || !this.installation.included) return 0;

    var subTotal =
      this.installationSummaryEntries.reduce(
        (total, entry) => total + entry.getTotal(),
        0
      ) +
      (this.installation.travelExpense
        ? this.installation.travelExpense.dNet
        : 0);
    return roundNumberToNearestCent(subTotal);
  }

  getInstallationSubTotalMsrp() {
    if (!this.supportsInstallation || !this.installation.included) return 0;

    var subTotal =
      this.installationSummaryEntries.reduce(
        (total, entry) => total + entry.getTotalMsrp(),
        0
      ) +
      (this.installation.travelExpense
        ? this.installation.travelExpense.msrp
        : 0);
    return roundNumberToNearestCent(subTotal);
  }

  getFreightSurcharge() {
    if (!this.canHaveFreightSurcharge) return 0;
    if (this.freightSurchargeOverridden) {
      return this.freightSurcharge;
    }

    const valueToCompare =
      this.getProductsSubtotal() + this.getCustomLineItemsSubTotal();
    return valueToCompare >= this.step1.dealerBranchSummary.minimumProductsValue
      ? 0
      : this.step1.dealerBranchSummary.freightSurchargeAmount;
  }

  getFreightSurchargeMsrp() {
    if (this.getFreightSurcharge() === 0) {
      return 0;
    } else {
      var freightSurchargeValue = this.freightSurchargeOverridden
        ? this.getFreightSurcharge()
        : this.step1.dealerBranchSummary.freightSurchargeAmount;
      return roundNumberToNearestCent(
        (freightSurchargeValue *
          (this.step1.dealerBranchSummary.msrpMarkupPercentage + 100)) /
          100
      );
    }
  }

  getSubTotal() {
    return (
      this.getProductsSubtotal() +
      this.getCustomLineItemsSubTotal() +
      this.getInstallationSubTotal() +
      this.getFreightSurcharge()
    );
  }

  getSubTotalMsrp() {
    return (
      this.getProductsSubtotalMsrp() +
      this.getCustomLineItemsSubTotalMsrp() +
      this.getInstallationSubTotalMsrp() +
      this.getFreightSurchargeMsrp()
    );
  }

  getDiscountedSubTotal() {
    let partialSubTotal = this.getSubTotal();
    this.overallDiscounts.forEach((discount) => {
      partialSubTotal -= discount.getDiscountAmount(partialSubTotal);
    });
    return partialSubTotal;
  }

  getDiscountSubTotalMsrp() {
    return this.getSubTotalMsrp();
  }

  getTax() {
    var beforeRounding =
      (this.getDiscountedSubTotal() * this.priceConfiguration.taxRate) / 100;
    var afterRounding = roundNumberToNearestCent(beforeRounding);
    return afterRounding;
  }

  getTaxMsrp() {
    return roundNumberToNearestCent(
      (this.getSubTotalMsrp() * this.priceConfiguration.taxRate) / 100
    );
  }

  getTotal() {
    return roundNumberToNearestCent(
      this.getDiscountedSubTotal() + this.getTax()
    );
  }

  getTotalMsrp() {
    return roundNumberToNearestCent(this.getSubTotalMsrp() + this.getTaxMsrp());
  }

  toQuoteInput() {
    const quoteInput = {
      ...this,
      step1: null,
      customLineItems: this.customLineItems.map((x) => x.getPayload()),
      installation: this.getInstallationPayload(),
      productsSubtotal: this.getProductsSubtotal(),
      installationSubTotal: this.getInstallationSubTotal(),
      customLineItemsSubTotal: this.getCustomLineItemsSubTotal(),
      tax: this.getTax(),
      total: this.getTotal(),
    };
    if (config.isManagementApp) return quoteInput;

    const clearUnauthorizedDealerOperarations = function (payload) {
      if (
        !(
          isAmiUserAuthorizedToManageDealerUsers() ||
          isUserDealerAdministrator()
        )
      )
        payload.dealerUserId = undefined;
      payload.customShippingLocation = undefined;
      payload.privateNotes = undefined;
      payload.customLineItems = undefined;
      payload.products.forEach(
        (x) => (x.buildToOrderLeadTimeOverride = undefined)
      );
      payload.products.forEach(
        (x) => (x.inventoryLeadTimeOverride = undefined)
      );
      return payload;
    };
    return clearUnauthorizedDealerOperarations(quoteInput);
  }

  create() {
    const payload = {
      ...this.toQuoteInput(),
      step1: this.step1,
    };
    return apiFetch(baseUrl, 'POST', payload, {
      automaticSuccessNotification: false,
    }).then((result) => {
      NotificationManager.success('Quote created');
      return result.id;
    });
  }

  update() {
    const payload = {
      ...this.toQuoteInput(),
      machineId: this.step1.machineId,
      manufacturer: this.step1.manufacturer,
      model: this.step1.model,
    };
    return apiFetch(`${baseUrl}/${this.id}`, 'PUT', payload, {
      automaticSuccessNotification: false,
    }).then((result) => {
      NotificationManager.success('Quote updated');
      return result.id;
    });
  }

  setStep1(step1) {
    this.step1 = step1;
    this.industryId = step1.industryId;
    this.industryName = step1.industryName;

    this.canHaveFreightSurcharge =
      step1 && step1.dealerBranchSummary
        ? step1.dealerBranchSummary.isFreightSurchargeEnabled
        : true;
  }

  static cancel(id) {
    return apiFetch(`${baseUrl}/${id}/cancel`, 'PUT', null, {
      automaticSuccessNotification: false,
    });
  }

  static expire(id) {
    return apiFetch(`${baseUrl}/${id}/expire`, 'PUT', null, {
      automaticSuccessNotification: false,
    });
  }

  static confirmLeadTimeChanges(id) {
    return apiFetch(`${baseUrl}/${id}/confirm-lead-time-changes`, 'POST', null);
  }

  static subscribe(id) {
    return apiFetch(`${subscriptionsBaseUrl}/${id}/subscribe`, 'POST', null);
  }

  static unsubscribe(id) {
    return apiFetch(`${subscriptionsBaseUrl}/${id}/unsubscribe`, 'POST', null);
  }

  static accept(id, acceptanceInfo) {
    return apiFetch(`${baseUrl}/accept`, 'POST', {
      ...acceptanceInfo,
      quoteId: id,
    });
  }

  static updateAcceptance(id, acceptanceInfo) {
    return apiFetch(`${baseUrl}/update-acceptance`, 'PUT', {
      ...acceptanceInfo,
      quoteId: id,
    });
  }

  static updateQuoteDiscountDocumentation(quoteId, quoteDiscountId, document) {
    return apiFetch(`${baseUrl}/${quoteId}/discount-documentation/${quoteDiscountId}`, 'PUT', {
      fileKey: document.documentationFileKey
    });
  }

  static changeDealerBranch(
    quoteId,
    dealerBranchId,
    dealerUserId,
    notificationSettings
  ) {
    return apiFetch(
      `${baseUrl}/${quoteId}/dealer-branch`,
      'PUT',
      {
        quoteId,
        dealerBranchId,
        dealerUserId,
        notificationSettings,
      },
      {
        automaticSuccessNotification: false,
      }
    ).then(() => NotificationManager.success('Dealer branch changed'));
  }

  static changePriceZone(quoteId, priceZoneId, notificationSettings) {
    return apiFetch(
      `${baseUrl}/${quoteId}/price-zone`,
      'PUT',
      {
        quoteId,
        priceZoneId,
        notificationSettings,
      },
      {
        automaticSuccessNotification: false,
      }
    ).then(() => NotificationManager.success('Price zone changed'));
  }

  static reSendCreateNotification(id, notificationSettings) {
    return apiFetch(`${baseUrl}/${id}/re-send`, 'PUT', notificationSettings, {
      automaticSuccessNotification: false,
    }).then(() => NotificationManager.success('Notification re-sent'));
  }

  static addCustomActivity(id, message) {
    return apiFetch(
      `${baseUrl}/${id}/activities`,
      'POST',
      { message },
      {
        automaticSuccessNotification: false,
      }
    ).then(() => NotificationManager.success('Custom activity added'));
  }

  static addRequestNewLeadTime(id, notes) {
    return apiFetch(
      `${baseUrl}/${id}/request-new-lead-time`,
      'POST',
      { notes },
      {
        automaticSuccessNotification: false,
      }
    ).then(() => NotificationManager.success('New lead time requested'));
  }

  static reOpen(id, notificationSettings) {
    return apiFetch(`${baseUrl}/${id}/re-open`, 'PUT', notificationSettings, {
      automaticSuccessNotification: false,
    });
  }

  static requestReOpen(id) {
    return apiFetch(`${baseUrl}/${id}/request-re-open`, 'PUT', null, {
      automaticSuccessNotification: false,
    }).then(() =>
      NotificationManager.success('Sent request to AMI to re-open quote')
    );
  }

  static copy(id, copyQuoteData, notificationSettings) {
    return apiFetch(
      `${baseUrl}/${id}/copy`,
      'PUT',
      { ...copyQuoteData, notificationSettings },
      {
        automaticSuccessNotification: false,
      }
    ).then((result) => {
      NotificationManager.success('Quote copied');
      return result.id;
    });
  }

  static async get(id) {
    const serverQuote = await apiFetch(`${baseUrl}/${id}`);
    const mappedQuote = this.mapFromServerEntity(serverQuote);
    return mappedQuote;
  }

  static mapFromServerEntity(serverQuote) {
    return Object.assign(new Quote(), serverQuote, {
      id: serverQuote.id,
      step1: new CreateQuoteStep1(serverQuote.step1Data),
      canHaveFreightSurcharge: serverQuote.canHaveFreightSurcharge,
      products: serverQuote.products.map((product) =>
        Object.assign(new QuoteProduct(product), {
          productOptions: product.productOptions.map(
            (x) => new QuoteProductOption(x)
          ),
          customizations: product.customizations.map(
            (x) => new QuoteProductCustomization(x)
          ),
          dimensions: product.dimensions.map(
            (x) => new QuoteProductDimension(x)
          ),
        })
      ),
      attachmentPoints: serverQuote.attachmentPoints.map((x) =>
        QuoteAttachmentPoint.fromServerEntity(x)
      ),
      customLineItems: serverQuote.customLineItems.map((x) =>
        QuoteCustomLineItem.fromServerEntity(x)
      ),
      installation: QuoteInstallation.fromServerEntity(
        serverQuote.installation
      ),
      overallDiscounts: serverQuote.overallDiscounts.map(
        (x) => new OverallDiscount(x)
      ),
      notificationSettings: new NotificationSettings(
        serverQuote.notificationSettings
      ),
      optInDiscounts: serverQuote.optInDiscounts.map((discount) =>
       Object.assign(new QuoteOptInDiscount(discount))
      ),
    });
  }

  static updatePartNumbers(id) {
    return apiFetch(`${baseUrl}/${id}/update-part-numbers`, 'PUT', null, {
      automaticSuccessNotification: false,
    });
  }
}
